import axios from "axios";
import io from "socket.io-client";

// Dynamically determine the backend URL based on current hostname
const getBackendURL = () => {
  const hostname = window.location.hostname;
  const port = "8800";

  // Localhost
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return `http://localhost:${port}`;
  }

  // Local network IP (mobile testing)
  if (
    hostname.match(/^192\.168\.\d+\.\d+$/) ||
    hostname.match(/^10\.\d+\.\d+\.\d+$/) ||
    hostname.match(/^172\.\d+\.\d+\.\d+$/)
  ) {
    return `http://${hostname}:${port}`;
  }

  // Production domains — call API on the same origin so cookies set with
  // domain=.clipbait.ai are accepted by the browser and shared with the
  // marketing site at clipbait.ai (presence cookie cb_session).
  if (hostname === "app.clipbait.ai" || hostname === "www.app.clipbait.ai") {
    return "https://app.clipbait.ai";
  }

  if (hostname === "clipbait-app.onrender.com") {
    return "https://clipbait-app.onrender.com";
  }

  // Default fallback
  return "https://clipbait-app.onrender.com";
};

const backendURL = getBackendURL();
console.log("🔗 Backend URL detected:", backendURL);

// -------------------- AXIOS CONFIG --------------------
export const axiosInstance = axios.create({
  baseURL: `${backendURL}/api`,
  withCredentials: true,
});

// 🔥 Attach JWT token automatically to every request
axiosInstance.interceptors.request.use(
  (config) => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (err) {
      console.warn("Failed to attach auth token:", err);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 🔄 Silently refresh expired access tokens
let isRefreshing = false;
let pendingQueue = [];

// When the user intentionally logs out, block any in-flight 401 from silently
// calling /auth/refresh — otherwise a stray polling/socket request can
// re-issue the refreshToken + cb_session presence cookies right after logout,
// leaving the marketing site at clipbait.ai still showing "My Dashboard".
let loggedOut = false;
export const setLoggedOut = (val) => { loggedOut = val; };

const flushQueue = (error, token = null) => {
  pendingQueue.forEach(({ resolve, reject }) => error ? reject(error) : resolve(token));
  pendingQueue = [];
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (
      error.response?.status !== 401 ||
      original._retry ||
      original.url?.includes("/auth/refresh") ||
      loggedOut
    ) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        pendingQueue.push({ resolve, reject });
      }).then((token) => {
        original.headers.Authorization = `Bearer ${token}`;
        return axiosInstance(original);
      });
    }

    original._retry = true;
    isRefreshing = true;

    try {
      const { data } = await axiosInstance.post("/auth/refresh");
      localStorage.setItem("token", data.token);
      axiosInstance.defaults.headers.common.Authorization = `Bearer ${data.token}`;
      original.headers.Authorization = `Bearer ${data.token}`;
      flushQueue(null, data.token);
      return axiosInstance(original);
    } catch (refreshError) {
      flushQueue(refreshError, null);
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      // Signal the app to log the user out
      window.dispatchEvent(new Event("auth:sessionExpired"));
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

// -------------------- SOCKET CONFIG --------------------
export const socketURL = backendURL;

export const socket = io(socketURL, {
  path: "/socket.io/",
  transports: ["websocket", "polling"],
  withCredentials: true,
  reconnection: true,
  reconnectionAttempts: 5,
  reconnectionDelay: 1000,
});
